import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { RoundedBox } from '@react-three/drei';
import * as THREE from 'three';
import { TILE_SIZE, OBSTACLE_HEIGHT, FLOOR_HEIGHT } from '../constants';
import { IceParticles } from '../shared/Particles3D';
import type { GraphicsQuality } from '../GameBoard3D';

interface StopTile3DProps {
  position: [number, number, number];
  entranceDelay?: number;
  graphics?: GraphicsQuality;
}

export default function StopTile3D({ position, entranceDelay = 0, graphics = 'high' }: StopTile3DProps) {
  const groupRef = useRef<THREE.Group>(null);
  const elapsed = useRef(0);
  const entered = useRef(false);

  const tileY = FLOOR_HEIGHT + OBSTACLE_HEIGHT / 2;
  const particleCount = graphics === 'high' ? 40 : graphics === 'medium' ? 20 : 10;

  useFrame((_, delta) => {
    if (!groupRef.current) return;
    elapsed.current += delta;

    if (!entered.current) {
      const t = Math.max(0, elapsed.current - entranceDelay);
      const progress = Math.min(t * 4, 1);
      groupRef.current.scale.set(progress, progress, progress);
      if (progress >= 1) entered.current = true;
    }
  });

  return (
    <group ref={groupRef} position={[position[0], 0, position[2]]} scale={0}>
      {/* Ice block with physical glass material
          (MeshTransmissionMaterial causes scene-wide artifacts with EffectComposer) */}
      <RoundedBox
        args={[TILE_SIZE * 0.88, OBSTACLE_HEIGHT, TILE_SIZE * 0.88]}
        radius={0.03}
        smoothness={3}
        position={[0, tileY, 0]}
      >
        <meshPhysicalMaterial
          color="#aaddff"
          transmission={0.85}
          thickness={0.5}
          roughness={0.1}
          ior={1.5}
          clearcoat={1}
          clearcoatRoughness={0.05}
          transparent
          opacity={0.9}
        />
      </RoundedBox>

      {/* Inner frosted wireframe core */}
      <mesh position={[0, tileY, 0]}>
        <icosahedronGeometry args={[TILE_SIZE * 0.3]} />
        <meshBasicMaterial color="#ffffff" wireframe transparent opacity={0.3} />
      </mesh>

      {/* Ice particles (scaled by quality) */}
      <group position={[0, tileY, 0]}>
        <IceParticles
          count={particleCount}
          bounds={[TILE_SIZE * 0.7, OBSTACLE_HEIGHT * 4, TILE_SIZE * 0.7] as [number, number, number]}
        />
      </group>
    </group>
  );
}
